import { dateTimeToView } from "helpers/date";
import { gasStationTypes, gasStationTypesEnum } from "helpers/enums";
import { enumLabel, formatNumber } from "helpers/system";
import { divIcon } from "leaflet";
import React from "react";
import { Circle, Marker, Tooltip } from "react-leaflet";

const DEFAULT_REFUELING_RADIUS = 200;

const RefuelingPoints = React.memo((props) => {
    const { refueling, refuelingServiceType, organization } = props;
    const { plannedRefuelings } = refueling;

    return plannedRefuelings.map((plannedRefueling, index) => (
        <RefuelingPoint
            key={index}
            plannedRefueling={plannedRefueling}
            refuelingServiceType={refuelingServiceType}
            organization={organization}
        />
    ));
});

const RefuelingPoint = ({ plannedRefueling, organization }) => {
    const {
        price,
        priceWithDiscount,
        name,
        fuelName,
        mapObject,
        resource,
        gasStationType,
        estimatedDate,
        autoSkippedAt,
        autoEnteredAt,
        autoLeftAt,
        trailerRefueling,
    } = plannedRefueling;

    const location = [mapObject.location.coordinates[1], mapObject.location.coordinates[0]];

    let fuelDisplayName = name;

    if (gasStationType === gasStationTypesEnum.Rosneft) {
        fuelDisplayName = enumLabel(gasStationTypes, gasStationType);
    }

    const options = {
        position: location,
        icon: divIcon(
            resource && resource.iconUrl
                ? {
                      html: `<img class="route-icon__refueling-logo" src="${resource.iconUrl}" />`,
                      className: "route-icon route-icon--refueling-wrap",
                      iconSize: [24, 24],
                  }
                : gasStationType === gasStationTypesEnum.Rosneft
                  ? {
                        className: "route-icon route-icon--refueling-default-service route-icon--refueling-rosneft",
                        iconSize: [24, 24],
                    }
                  : { className: "route-icon route-icon--refueling", iconSize: [24, 24] },
        ),
    };

    const renderVolumeInfo = ({
        requiredLoadVolume,
        planVolumeBeforeRefueling,
        factVolumeBeforeRefueling,
        planVolumeAfterRefueling,
        factVolumeAfterRefueling,
        factLoadVolume,
    }) => {
        return (
            <>
                <div className="b-timeline-point__date--name">Топливо</div>
                <div className="b-timeline-point__date--value">
                    {fuelName}, {formatNumber(Math.round(requiredLoadVolume))} л.
                </div>
                <div className="b-timeline-point__date--name">Кол-во топлива до заправки (план)</div>
                <div className="b-timeline-point__date--value">
                    {formatNumber(Math.round(planVolumeBeforeRefueling))} л.
                </div>
                {factVolumeBeforeRefueling && (
                    <>
                        <div className="b-timeline-point__date--name">Кол-во топлива до заправки (факт)</div>
                        <div className="b-timeline-point__date--value">
                            {formatNumber(Math.round(factVolumeBeforeRefueling))} л.
                        </div>
                    </>
                )}
                <div className="b-timeline-point__date--name">Кол-во топлива после заправки (план)</div>
                <div className="b-timeline-point__date--value">
                    {formatNumber(Math.round(planVolumeAfterRefueling))} л.
                </div>
                {factVolumeAfterRefueling && (
                    <>
                        <div className="b-timeline-point__date--name">Кол-во топлива после заправки (факт)</div>
                        <div className="b-timeline-point__date--value">
                            {formatNumber(Math.round(factVolumeAfterRefueling))} л.
                        </div>
                    </>
                )}
                {factLoadVolume && (
                    <>
                        <div className="b-timeline-point__date--name">Заправлено топлива по факту</div>
                        <div className="b-timeline-point__date--value">
                            {formatNumber(Math.round(factLoadVolume))} л.
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <Marker {...options}>
                <Tooltip
                    className="map__tooltip-route-marker leaflet-tooltip--left-align leaflet-tooltip--light"
                    direction="top"
                >
                    <div className="route-icon__time-tooltip--header pb-1">
                        <span className="b-timeline-point__city--name">{fuelDisplayName}</span>
                    </div>
                    <div className="b-timeline-point__city--full pb-2">{mapObject.address}</div>
                    <div className="b-timeline-point__table b-timeline-point__table--double-column b-timeline-point__table--map">
                        <div className="b-timeline-point__date--name">Стоимость</div>
                        <div className="b-timeline-point__date--value">
                            {formatNumber(price, { style: "currency", currency: organization?.currency })}
                        </div>
                        {priceWithDiscount && (
                            <>
                                <div className="b-timeline-point__date--name">Стоимость со скидкой</div>
                                <div className="b-timeline-point__date--value">
                                    {formatNumber(priceWithDiscount, {
                                        style: "currency",
                                        currency: organization?.currency,
                                    })}
                                </div>
                            </>
                        )}
                        {autoSkippedAt && (
                            <>
                                <div className="b-timeline-point__date--name">Дата пропуска</div>
                                <div className="b-timeline-point__date--value">{dateTimeToView(autoSkippedAt)}</div>
                            </>
                        )}
                        {estimatedDate && (
                            <>
                                <div className="b-timeline-point__date--name">Расчетная дата въезда</div>
                                <div className="b-timeline-point__date--value">{dateTimeToView(estimatedDate)}</div>
                            </>
                        )}
                        {autoEnteredAt && (
                            <>
                                <div className="b-timeline-point__date--name">Время въезда по GPS</div>
                                <div className="b-timeline-point__date--value">{dateTimeToView(autoEnteredAt)}</div>
                            </>
                        )}
                        {autoLeftAt && (
                            <>
                                <div className="b-timeline-point__date--name">Время выезда по GPS</div>
                                <div className="b-timeline-point__date--value">{dateTimeToView(autoLeftAt)}</div>
                            </>
                        )}
                        {!!plannedRefueling.requiredLoadVolume && (
                            <>
                                <div className="b-timeline-point__table--row font-size-14 font-weight-bold pt-2">
                                    Машина
                                </div>
                                {renderVolumeInfo(plannedRefueling)}
                            </>
                        )}
                        {trailerRefueling && (
                            <>
                                <div className="b-timeline-point__table--row font-size-14 font-weight-bold pt-2">
                                    Прицеп
                                </div>
                                {renderVolumeInfo(trailerRefueling)}
                            </>
                        )}
                    </div>
                </Tooltip>
            </Marker>
            <Circle center={location} radius={mapObject.radius || DEFAULT_REFUELING_RADIUS} />
        </>
    );
};

export default RefuelingPoints;
